/**
 * T1155 — shramba nastavitev pogleda BoQ **po projektu**.
 *
 * Zahteva (Alen, 26.7.2026): ko uporabnik ustvari nov projekt in ga odpre,
 * morata biti »Polna cena« in »Prodajna cena« izklopljena. VEDNO. Zato ti dve
 * nastavitvi nikoli ne podedujeta zadnje uporabljene vrednosti — gl.
 * `NEVER_INHERITED`. Kozmetične nastavitve (gostota, koda, opis, Σ/%) se v nov
 * projekt podedujejo, ker ne razkrivajo cen.
 *
 * ⚠️ Ob dodajanju NOVE nastavitve pogleda: dodaj polje v `BoqViewPrefs` +
 * `FACTORY_VIEW_PREFS` in se odloči, ali sodi v `NEVER_INHERITED` — vse, kar
 * razkriva cene ali maržo, tja sodi.
 *
 * Ta modul je EDINI lastnik ključa `boq-view-prefs`; nihče drug ga ne bere ali
 * piše. `uiStore` ga uvaža, zato je uvoz tipov iz `uiStore` sem `import type`
 * (drugače nastane ciklična runtime odvisnost).
 */
import type {
  BoqDensity,
  CodeDisplayMode,
  OverheadView,
  SectionAmountView,
  SellingView,
} from '../store/uiStore';

export interface BoqViewPrefs {
  overheadView: OverheadView;
  sellingView: SellingView;
  boqDensity: BoqDensity;
  codeDisplayMode: CodeDisplayMode;
  showItemDescription: boolean;
  /** T2429: slike detajlov pod postavko (privzeto vidne). */
  showDetailImages: boolean;
  sectionAmountView: SectionAmountView;
}

export const FACTORY_VIEW_PREFS: BoqViewPrefs = {
  overheadView: 'off',
  sellingView: 'off',
  boqDensity: 'standard',
  codeDisplayMode: 'code',
  showItemDescription: false,
  showDetailImages: true,
  sectionAmountView: 'sum',
};

/** Nastavitve, ki se NIKOLI ne podedujejo v projekt, odprt prvič (T1155). */
export const NEVER_INHERITED = ['overheadView', 'sellingView'] as const;

export const VIEW_PREFS_STORAGE_KEY = 'boq-view-prefs';
export const MAX_PROJECTS = 50;

type NeverInheritedKey = (typeof NEVER_INHERITED)[number];
export type InheritablePrefs = Partial<Omit<BoqViewPrefs, NeverInheritedKey>>;

interface PrefsStore {
  v: 1;
  /** ključ = projectId kot string (JSON nima številčnih ključev) */
  byProject: Record<string, BoqViewPrefs>;
  lastUsed: InheritablePrefs;
  /** MRU najprej — vir resnice za LRU obrez (namenoma brez časovnih žigov) */
  order: number[];
}

function emptyStore(): PrefsStore {
  return { v: 1, byProject: {}, lastUsed: {}, order: [] };
}

/** Odstrani cenovna polja — edino mesto, ki uveljavlja invariant T1155. */
function stripNeverInherited<T extends Partial<BoqViewPrefs>>(prefs: T): InheritablePrefs {
  const out = { ...prefs } as Record<string, unknown>;
  for (const key of NEVER_INHERITED) delete out[key];
  return out as InheritablePrefs;
}

// ── Migracija starih globalnih ključev ───────────────────────────────────────
// Kozmetične preference prenesemo (uporabnik jih ne izgubi), cenovni ključi
// (`boq-overhead-view`, `boq-selling-view` in prastara `boq-show-overhead`,
// `boq-show-selling-price`) se NAMERNO zavržejo — posledica zahteve »Vedno!«.
// Starih ključev ne brišemo (varovalka za rollback); čiščenje = kasnejši ticket.

function migrateLegacyKeys(): InheritablePrefs {
  const out: InheritablePrefs = {};

  const density = localStorage.getItem('boq-density');
  if (density === 'compact' || density === 'standard' || density === 'comfortable') {
    out.boqDensity = density;
  }

  const code = localStorage.getItem('boq-code-display-mode');
  if (code === 'code' || code === 'code_f') out.codeDisplayMode = code;

  const description = localStorage.getItem('boq-show-item-description');
  if (description === 'true' || description === 'false') {
    out.showItemDescription = description === 'true';
  }

  const amount = localStorage.getItem('boq-section-amount-view');
  if (amount === 'sum' || amount === 'percent') out.sectionAmountView = amount;

  return out;
}

// ── Branje / pisanje ────────────────────────────────────────────────────────

function loadStore(): PrefsStore {
  let raw: string | null;
  try {
    raw = localStorage.getItem(VIEW_PREFS_STORAGE_KEY);
  } catch {
    return emptyStore();
  }

  if (raw === null) {
    const migrated = emptyStore();
    migrated.lastUsed = migrateLegacyKeys();
    return migrated;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<PrefsStore> | null;
    if (!parsed || parsed.v !== 1 || typeof parsed.byProject !== 'object' || parsed.byProject === null) {
      return emptyStore();
    }
    return {
      v: 1,
      byProject: parsed.byProject as Record<string, BoqViewPrefs>,
      lastUsed: stripNeverInherited((parsed.lastUsed ?? {}) as Partial<BoqViewPrefs>),
      order: Array.isArray(parsed.order) ? parsed.order.filter((id) => typeof id === 'number') : [],
    };
  } catch {
    // Nastavitve pogleda niso vredne bele strani — tiho na tovarniško.
    return emptyStore();
  }
}

function saveStore(store: PrefsStore): void {
  try {
    localStorage.setItem(VIEW_PREFS_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Zapolnjena kvota / zasebni način — nastavitev pogleda ni kritična.
  }
}

/** Začetno stanje store-a PRED hidracijo: tovarniško + podedljive zadnje uporabljene. */
export function readInitialPrefs(): BoqViewPrefs {
  return { ...FACTORY_VIEW_PREFS, ...loadStore().lastUsed };
}

/** Nastavitve za dani projekt; za neznan projekt velja pravilo dedovanja (T1155). */
export function readForProject(projectId: number): BoqViewPrefs {
  const store = loadStore();
  const saved = store.byProject[String(projectId)];
  if (saved) return { ...FACTORY_VIEW_PREFS, ...saved };
  return { ...FACTORY_VIEW_PREFS, ...store.lastUsed };
}

/** Zapiše delni patch za projekt + osveži `lastUsed` (samo podedljiva polja) in LRU. */
export function writeForProject(projectId: number, patch: Partial<BoqViewPrefs>): void {
  const store = loadStore();
  const key = String(projectId);
  const current = store.byProject[key] ?? { ...FACTORY_VIEW_PREFS, ...store.lastUsed };

  store.byProject[key] = { ...current, ...patch };
  store.lastUsed = { ...store.lastUsed, ...stripNeverInherited(patch) };
  store.order = [projectId, ...store.order.filter((id) => id !== projectId)];

  if (store.order.length > MAX_PROJECTS) {
    for (const evicted of store.order.slice(MAX_PROJECTS)) {
      delete store.byProject[String(evicted)];
    }
    store.order = store.order.slice(0, MAX_PROJECTS);
  }

  saveStore(store);
}

/** Setter zunaj projektnega konteksta (`activeViewProjectId === null`) — samo `lastUsed`. */
export function writeLastUsedOnly(patch: Partial<BoqViewPrefs>): void {
  const store = loadStore();
  store.lastUsed = { ...store.lastUsed, ...stripNeverInherited(patch) };
  saveStore(store);
}
